import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo } from 'react';
import { postEvidencesBulk } from '../api/evidence-details';
import type { EnrichedEvidence } from '../components/explore/discovery-evidence/types';

const DEFAULT_PAGE_SIZE = 15;

function stableEvidenceIdsKey(ids: string[]): string {
  return [...ids].sort().join(',');
}

export interface UseEvidenceListDataReturn {
  evidences: EnrichedEvidence[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  total: number;
  error: Error | null;
}

export function useEvidenceListData(
  evidenceIds: string[],
  pageSize: number = DEFAULT_PAGE_SIZE
): UseEvidenceListDataReturn {
  const auth0 = useAuth0();
  const sortedKey = stableEvidenceIdsKey(evidenceIds);

  const query = useInfiniteQuery({
    queryKey: ['evidences-bulk', sortedKey, pageSize],
    queryFn: async ({ pageParam }) => {
      const page = typeof pageParam === 'number' ? pageParam : 0;
      return postEvidencesBulk(
        {
          evidenceIds,
          page,
          pageSize,
        },
        auth0
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    enabled: evidenceIds.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const evidences = useMemo(() => {
    const pages = query.data?.pages ?? [];
    return pages.flatMap((page) => page.evidences as EnrichedEvidence[]);
  }, [query.data?.pages]);

  const total = query.data?.pages?.[0]?.total ?? 0;

  return {
    evidences,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    total,
    error: (query.error as Error) ?? null,
  };
}
